import React, { useEffect, useState } from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Paper,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import AppLayout from "../../components/AppLayout/AppLayout";
import ButtonContained from "../../components/StyledComponents/ButtonContained";
import ButtonOutlined from "../../components/StyledComponents/ButtonOutlined";
import AttachmentIcon from "@mui/icons-material/Attachment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from '@mui/icons-material/Visibility';
import httpRequest from "../../httpRequest";

const DocumentUpload = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: "",
    description: "",
    filename: "",
    file: null,
    category: null,
    company: null,
    function: null,
  });

  const fetchCurrentUser = async () => {
    try {
      const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/@me?exclude_fields=avatar,signature`);
      if (response.data) {
        return response.data.user_id;
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch user information", { variant: "error" });
    }
    return null;
  };

  const fetchMetadata = async () => {
    try {
      const [categoriesRes, companiesRes, functionsRes] = await Promise.all([
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/document-categories`),
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies`),
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/functions`),
      ]);
      setCategories(categoriesRes.data);
      setCompanies(companiesRes.data.companies);
      setFunctions(functionsRes.data.functions);
    } catch (error) {
      enqueueSnackbar("Failed to fetch metadata", { variant: "error" });
    }
  };

  useEffect(() => {
    fetchMetadata();
  }, []);

  
const handleFileChange = (e) => {
  const file = e.target.files[0];
  const reader = new FileReader();
  reader.onload = () => {
    const base64Data = reader.result.split(",")[1];
    setNewDocument({ ...newDocument, file: base64Data, filename: file.name });
  };
  reader.readAsDataURL(file);
};


const handleUpload = async () => {
  if (!newDocument.file || !newDocument.name || !newDocument.category) {
    enqueueSnackbar("Please fill all required fields", { variant: "warning" });
    return;
  }

  const userId = await fetchCurrentUser();
  if (!userId) {
    enqueueSnackbar("Failed to retrieve user information", { variant: "error" });
    return;
  }

  const payload = {
    name: newDocument.name,
    description: newDocument.description,
    filename: newDocument.filename,
    file: newDocument.file, // This is Base64
    document_category_id: newDocument.category.id,
    company_id: newDocument.company?.id,
    function_id: newDocument.function?.id,
    uploaded_by_user_id: userId,
  };

  try {
    await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/documents`, payload);
    enqueueSnackbar("Document uploaded successfully", { variant: "success" });
    setNewDocument({
      name: "",
      description: "",
      file: null,
      filename: "",
      category: null,
      company: null,
      function: null,
    });
  } catch (error) {
    enqueueSnackbar("Failed to upload document", { variant: "error" });
  }
};



  return (
    <AppLayout>
      <Grid container justifyContent="center" spacing={4}>
        <Grid item xs={10.8}>
          <Paper elevation={3} sx={{ padding: 3, marginBottom: 4 }}>
            <Typography variant="h6">Upload New Document</Typography>
            <TextField
              label="Document Name"
              value={newDocument.name}
              onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Description"
              value={newDocument.description}
              onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
              fullWidth
              margin="normal"
            />
             <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name}
              value={newDocument.category || null}
              onChange={(e, newValue) => setNewDocument({ ...newDocument, category: newValue })}
              renderInput={(params) => <TextField {...params} label="Category" fullWidth margin="normal" />}
            />
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.company_name}
              value={newDocument.company || null}
              onChange={(e, newValue) => setNewDocument({ ...newDocument, company: newValue })}
              renderInput={(params) => <TextField {...params} label="Company" fullWidth margin="normal" />}
            />
            <Autocomplete
              options={functions}
              getOptionLabel={(option) => option.function_name}
              value={newDocument.function || null}
              onChange={(e, newValue) => setNewDocument({ ...newDocument, function: newValue })}
              renderInput={(params) => <TextField {...params} label="Function" fullWidth margin="normal" />}
            />
           
            <AttachmentIcon /> Select File
           <input type="file" accept="*/*" onChange={handleFileChange} />

            <Typography variant="caption">
              {newDocument.filename ? `Selected file: ${newDocument.filename}` : "No file selected"}
            </Typography>
            <ButtonContained onClick={handleUpload} fullWidth sx={{ marginTop: 2 }}>
              Upload
            </ButtonContained>
          </Paper>
        </Grid>
      </Grid>

    
    </AppLayout>
  );
};

export default DocumentUpload;
